import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ChevronRight,
  MapPin,
  Clock,
  Shield,
  Sparkles,
  CheckCircle,
  Users,
  Smile
} from 'lucide-react';
import { howItWorksSteps, busyDaysFeatures } from '../data/mock';

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden jolly-hero-gradient py-16 md:py-24">
        <div className="absolute top-10 right-10 w-20 h-20 rounded-full bg-[hsl(45,98%,80%)] opacity-50 sun-pulse" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold jolly-text-teal mb-6">
              How It Works
            </h1>
            <p className="text-gray-600 text-lg md:text-xl">
              Every parent deserves a stress-free shopping experience. Here's how Jolly Tots Rides makes it happen.
            </p>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16 md:space-y-24">
            {howItWorksSteps.map((step, index) => (
              <div 
                key={step.id} 
                className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                {/* Content */}
                <div className={`space-y-4 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="flex items-center gap-4">
                    <div className="step-badge bg-[hsl(33,100%,50%)] text-white text-2xl">
                      {step.step}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold jolly-text-teal">
                      {step.title}
                    </h2>
                  </div>
                  <p className="text-gray-600 text-lg leading-relaxed pl-16">
                    {step.description}
                  </p>
                  {step.id === 1 && (
                    <div className="pl-16 space-y-2">
                      <div className="flex items-center gap-2 text-gray-500">
                        <MapPin className="w-4 h-4 jolly-text-orange" />
                        <span className="text-sm">Look for the Jolly Bay signage at mall entrances</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <Clock className="w-4 h-4 jolly-text-orange" />
                        <span className="text-sm">Open during mall operating hours</span>
                      </div>
                    </div>
                  )}
                  {step.id === 2 && (
                    <div className="pl-16 space-y-2">
                      <div className="flex items-center gap-2 text-gray-500">
                        <Shield className="w-4 h-4 jolly-text-blue" />
                        <span className="text-sm">5-point safety harness included</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <Users className="w-4 h-4 jolly-text-blue" />
                        <span className="text-sm">Trained attendants assist with buckling</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Illustration */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="bg-gradient-to-br from-[hsl(45,90%,90%)] to-[hsl(35,80%,85%)] rounded-3xl overflow-hidden aspect-[4/3] flex items-center justify-center">
                    <div className="text-center w-full h-full">
                      {step.id === 1 && (
                        <img 
                          src="https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/v8x3gz9z_ChatGPT%20Image%20Jan%2016%2C%202026%2C%2010_42_17%20AM.png"
                          alt="Jolly Bay Branded Station"
                          className="w-full h-full object-cover"
                        />
                      )}
                      {step.id === 2 && (
                        <img 
                          src="https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/klyn36l4_ChatGPT%20Image%20Jan%2016%2C%202026%2C%2010_42_24%20AM.png"
                          alt="Secure Your Child"
                          className="w-full h-full object-cover"
                        />
                      )}
                      {step.id === 3 && (
                        <img 
                          src="https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/fvqgr5bg_ChatGPT%20Image%20Jan%2016%2C%202026%2C%2010_42_29%20AM.png"
                          alt="Shop with Confidence"
                          className="w-full h-full object-cover"
                        />
                      )}
                      {step.id === 4 && (
                        <img 
                          src="https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/nuac4nec_New%20Santize%20pic.png"
                          alt="Easy Return & Sanitisation"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Busy Shopping Days Section */}
      <section className="py-16 md:py-24 jolly-gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="jolly-section-title">
                Designed for Busy Shopping Days:
              </h2>
              <div className="space-y-4">
                {busyDaysFeatures.map((feature) => (
                  <div key={feature.id} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[hsl(33,100%,50%)] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <div className="text-center space-y-4">
                <div className="inline-block p-4 bg-[hsl(45,100%,90%)] rounded-full">
                  <Sparkles className="w-12 h-12 jolly-text-orange" />
                </div>
                <h3 className="text-xl font-bold jolly-text-teal">Peak Time Ready</h3>
                <p className="text-gray-600">
                  Our operations are designed to handle high-traffic periods like weekends, holidays, and school breaks with additional trolleys and staff.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[hsl(210,90%,55%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Try Jolly Tots Rides?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Find us at participating malls and make your next shopping trip a breeze.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/parents">
              <Button className="bg-white text-[hsl(210,90%,45%)] hover:bg-gray-100 font-bold rounded-full px-8 py-4 text-base shadow-lg">
                View Pricing
              </Button>
            </Link>
            <Link to="/safety">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold rounded-full px-8 py-4 text-base">
                Learn About Safety
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
